export interface CsvColumn {
  key: string;
  label: string;
}

export type CsvRow = Record<string, unknown>;

const FORMULA_PREFIX = /^[\t\r\n ]*[=+\-@]/u;
const INVALID_FILENAME = /[<>:"/\\|?*\u0000-\u001f]/gu;

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(cellText).filter(Boolean).join('；');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function escapeCsvCell(value: unknown): string {
  const raw = cellText(value);
  const formulaSafeType = typeof value === 'number' || typeof value === 'boolean';
  const safe = !formulaSafeType && FORMULA_PREFIX.test(raw) ? `'${raw}` : raw;
  return `"${safe.replaceAll('"', '""')}"`;
}

export function buildCsv(columns: readonly CsvColumn[], rows: readonly CsvRow[]): string {
  const header = columns.map((column) => escapeCsvCell(column.label)).join(',');
  const body = rows.map((row) =>
    columns.map((column) => escapeCsvCell(row[column.key])).join(','));
  return `\uFEFF${[header, ...body].join('\r\n')}\r\n`;
}

export function safeFilenamePart(value: unknown, fallback = '数据'): string {
  const cleaned = String(value ?? '')
    .replace(INVALID_FILENAME, '-')
    .replace(/\s+/gu, ' ')
    .replace(/[. ]+$/gu, '')
    .trim();
  return cleaned || fallback;
}

export function buildExportFilename(
  parts: readonly unknown[],
  date: Date = new Date(),
): string {
  const stamp = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('');
  return `${parts.map((part) => safeFilenamePart(part)).join('_')}_${stamp}.csv`;
}

export function downloadCsv(
  filename: string,
  columns: readonly CsvColumn[],
  rows: readonly CsvRow[],
): void {
  const blob = new Blob([buildCsv(columns, rows)], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeFilenamePart(filename, '导出数据.csv');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
